import React, { useEffect, useState, useCallback, useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, Copy, ShieldCheck, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";
import api, { formatIDR } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export function PaymentModal({ order, open, onClose, onPaid }) {
  const { t } = useI18n();
  const [status, setStatus] = useState(order?.status || "pending");
  const [checking, setChecking] = useState(false);
  const [seconds, setSeconds] = useState(300);
  const [imgError, setImgError] = useState(false);
  const timerRef = useRef(null);

  const check = useCallback(async () => {
    if (!order?.id) return;
    setChecking(true);
    try {
      const { data } = await api.get(`/orders/${order.id}/status`);
      setStatus(data.order.status);
      if (data.order.status === "paid") {
        onPaid?.();
      }
    } catch (_) {
    } finally {
      setChecking(false);
    }
  }, [order, onPaid]);

  useEffect(() => {
    if (!open || !order) return;
    setStatus(order.status || "pending");
    setSeconds(300);
    setImgError(false);
    const poll = setInterval(check, 4000);
    const countdown = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    timerRef.current = { poll, countdown };
    return () => {
      clearInterval(poll);
      clearInterval(countdown);
    };
  }, [open, order, check]);

  if (!order) return null;

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  const qrImage = null;
  const qrData = order.qr_data;

  const copyQris = () => {
    if (qrData) {
      navigator.clipboard.writeText(qrData);
      toast.success(t("checkout.copyQris"));
    }
  };

  const paid = status === "paid";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="payment-modal">
        <DialogTitle className="font-display text-xl font-bold text-slate-900">
          {paid ? t("checkout.paidTitle") : t("checkout.scanTitle")}
        </DialogTitle>
        <DialogDescription className="text-sm text-slate-500">
          {paid ? t("checkout.success") : t("checkout.scanDesc")}
        </DialogDescription>

        {paid ? (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex flex-col items-center py-8"
          >
            <CheckCircle2 size={72} className="text-emerald-500" />
            <p className="mt-4 font-mono text-2xl font-extrabold text-slate-900">{formatIDR(order.total)}</p>
          </motion.div>
        ) : (
          <>
            <div className="flex flex-col items-center">
              <div data-testid="qris-container">
                {qrImage && !imgError ? (
                  <img
                    src={qrImage}
                    alt="QRIS"
                    className="h-60 w-60 object-contain"
                    data-testid="qris-image"
                    onError={() => setImgError(true)}
                  />
                ) : qrData ? (
                  <QRCodeSVG value={qrData} size={240} level="M" includeMargin={false} data-testid="qris-svg" />
                ) : (
                  <div className="flex h-60 w-60 items-center justify-center text-center text-sm text-slate-400">
                    {t("checkout.gatewayError")}
                  </div>
                )}
              </div>
              <p className="mt-4 font-mono text-2xl font-extrabold text-slate-900" data-testid="qris-amount">
                {formatIDR(order.total)}
              </p>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
                <span>{t("checkout.orderNo")}: {order.mch_order_no}</span>
              </div>
              <div className="mt-2 flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                {t("checkout.expiresIn")} {mmss}
              </div>
            </div>

            {qrData && (
              <button
                onClick={copyQris}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
                data-testid="copy-qris-btn"
              >
                <Copy size={14} /> {t("checkout.copyQris")}
              </button>
            )}

            <button
              onClick={check}
              disabled={checking}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition disabled:opacity-60"
              data-testid="check-status-btn"
            >
              {checking ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              {t("checkout.checkStatus")}
            </button>

            <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
              <ShieldCheck size={13} className="text-emerald-500" /> Tarapay Secure Payment · QRIS
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
